import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ledgerly_token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('ledgerly_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  // Handle 401 responses (logout) and 403 ACCESS_DENIED (force refresh of user)
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || '';
      if (status === 401) {
        logout();
      } else if (status === 403 && typeof detail === 'string' && detail.startsWith('ACCESS_DENIED')) {
        // Refresh user state so router can redirect to subscription/pending pages
        fetchUser();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token } = response.data;
    localStorage.setItem('ledgerly_token', access_token);
    setToken(access_token);
    await fetchUser();
    return response.data;
  };

  const register = async (email, password) => {
    const response = await api.post('/auth/register', { email, password });
    const { access_token } = response.data;
    localStorage.setItem('ledgerly_token', access_token);
    setToken(access_token);
    await fetchUser();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('ledgerly_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = fetchUser;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
